/**
 * Apply clipboard payloads back into a live FormulaConfig.
 *
 * Generates fresh ids on paste, recomputes group expressions, and rebalances
 * the per-layer group weights so every layer sums to 100 and the result is
 * immediately consistent with the rest of the optimizer. Legacy v1 payloads
 * (single flat weight per group) are expanded to the per-layer shape.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { migrateBlocksBody, parseExpressionToBlock } from '@trafficpulse/shared';
import type {
  FlowLayer,
  FormulaConfig,
  FormulaGroup,
  GroupWeights,
  PerLayerGroupWeight,
} from '@trafficpulse/shared';

import { recomputeGroupExpression } from '../tabs/calculation/formula-group.utils';
import {
  groupLayers,
  rebalanceConfig,
} from '../tabs/calculation/per-layer-weights.utils';

import type {
  FormulaClipboardPayload,
  FormulaConfigPayload,
  FormulaGroupPayload,
  FormulaWeightPayload,
} from './formula-clipboard.types';

function freshId(seed: number): string {
  return `g_${Date.now().toString(36)}_${seed.toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

/** Skips that still mean something here: a bank id names a row of ONE flow, so
 *  anything the pasted-into flow does not have is dropped rather than kept as a
 *  skip that protects nobody. Omitted entirely when `known` is not supplied. */
function keptSkips(
  payload: FormulaGroupPayload,
  known?: ReadonlySet<string>,
): string[] {
  if (!payload.ignoredPathBankIds?.length || !known) return [];
  return payload.ignoredPathBankIds.filter((id) => known.has(id));
}

function payloadToGroup(
  payload: FormulaGroupPayload,
  id: string,
  known?: ReadonlySet<string>,
): FormulaGroup {
  const skips = keptSkips(payload, known);
  // AI / hand-written payloads carry expression-only blocks groups. Without a
  // body the master formula renders EMPTY (recompute reads masterExpression),
  // so synthesize one exactly like the backend read normalizer does.
  const blocks =
    payload.mode === 'blocks'
      ? migrateBlocksBody(
          payload.blocks ?? {
            ast: parseExpressionToBlock(payload.expression ?? ''),
          },
          payload.name || 'Subformula 1',
        )
      : payload.blocks;
  const group: FormulaGroup = {
    id,
    name: payload.name,
    dateRange: payload.dateRange,
    dateRangeCustomFrom: payload.dateRangeCustomFrom,
    dateRangeCustomTo: payload.dateRangeCustomTo,
    mode: payload.mode,
    sliders: payload.sliders,
    blocks,
    expression: payload.expression,
    appliesTo: payload.appliesTo,
    ...(skips.length ? { ignoredPathBankIds: skips } : {}),
  };
  return { ...group, expression: recomputeGroupExpression(group) };
}

export function appendGroupFromPayload(
  formula: FormulaConfig,
  payload: FormulaGroupPayload,
  known?: ReadonlySet<string>,
): FormulaConfig {
  const id = freshId(formula.groups.length);
  const group = payloadToGroup(payload, id, known);
  // rebalanceConfig reads existing per-layer weights and seeds the new group.
  return rebalanceConfig({ ...formula, groups: [...formula.groups, group] });
}

export function replaceFormulaFromPayload(
  payload: FormulaConfigPayload,
  known?: ReadonlySet<string>,
): FormulaConfig {
  const groups: FormulaGroup[] = payload.groups.map((g, idx) =>
    payloadToGroup(g, freshId(idx), known),
  );
  const groupWeights = expandPayloadWeights(payload.groupWeights, groups);
  return rebalanceConfig({ groups, groupWeights });
}

/** v1 (flat number) or v2 (per-layer) payload weights -> per-layer GroupWeights. */
function expandPayloadWeights(
  weights: ReadonlyArray<FormulaWeightPayload>,
  groups: ReadonlyArray<FormulaGroup>,
): GroupWeights {
  const out: Record<string, PerLayerGroupWeight> = {};
  groups.forEach((group, idx) => {
    const raw = weights[idx];
    const entry: PerLayerGroupWeight = {};
    for (const layer of groupLayers(group)) entry[layer] = readWeight(raw, layer);
    out[group.id] = entry;
  });
  return out;
}

function readWeight(
  raw: FormulaWeightPayload | undefined,
  layer: FlowLayer,
): number {
  if (typeof raw === 'number') return raw;
  const value = raw?.[layer];
  return typeof value === 'number' ? value : 0;
}

export function applyClipboardPayload(
  formula: FormulaConfig,
  payload: FormulaClipboardPayload,
  /** Bank ids the pasted-into flow has right now — see `keptSkips`. */
  known?: ReadonlySet<string>,
): FormulaConfig {
  if (payload.type === 'group') {
    return appendGroupFromPayload(
      formula,
      payload.data as FormulaGroupPayload,
      known,
    );
  }
  return replaceFormulaFromPayload(payload.data as FormulaConfigPayload, known);
}
